import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { ArrowRight, Wand2, Loader2, Sparkles } from "lucide-react";

/**
 * Derive a human-readable primary reference from a sample record.
 * Prefers the new generic `primary_ref` field; falls back to description
 * or record_id for backward-compatibility with older responses.
 */
function resolvePrimaryRef(s) {
  if (s.primary_ref) return s.primary_ref;
  if (s.record_id && s.record_id !== "0") return s.record_id;
  return s.description || "—";
}

/**
 * Derive a human-readable entity label from a sample record.
 * Prefers the new generic `entity_ref`; falls back gracefully.
 */
function resolveEntityRef(s) {
  return s.entity_ref || "—";
}

/**
 * Derive an amount label from a sample record.
 * Prefers `amount_ref`; falls back to "—" if absent or empty.
 */
function resolveAmountRef(s) {
  const val = s.amount_ref;
  if (val === null || val === undefined || val === "") return "—";
  return val;
}

export default function Phase6Sampling() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun } = useAudit();
  const [sampleSize, setSampleSize] = useState(10);
  const [populationFallback, setPopulationFallback] = useState("");
  const [method, setMethod] = useState("random");
  const [generating, setGenerating] = useState(false);
  const [sampling, setSampling] = useState(null);
  const displayMethod = (sampling?.method || method || "").replace(/_/g, " ");

  useEffect(() => {
    if (auditRun?.sampling) {
      setSampling(auditRun.sampling);
      setSampleSize(auditRun.sampling.sample_size || auditRun.sampling.recommended_sample_size || 10);
      setPopulationFallback(auditRun.sampling.population_size ? String(auditRun.sampling.population_size) : "");
      setMethod(auditRun.sampling.method || "random");
    }
  }, [auditRun?.sampling]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {
        sample_size: sampleSize,
        method
      };
      const parsedPopulationFallback = parseInt(populationFallback, 10);
      if (parsedPopulationFallback > 0) {
        payload.population_size = parsedPopulationFallback;
      }
      const { data } = await API.post(`/audit-runs/${auditRun.id}/sampling`, {
        ...payload
      });
      setSampling(data);
      await refreshRun();
      toast.success("Sampling strategy generated");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to generate sampling");
    }
    setGenerating(false);
  };

  const handleContinue = async () => {
    await advancePhase(7);
    navigate("/phase/7");
    toast.success("Proceeding to Evidence Evaluation");
  };

  // Infer column header labels from the first sample in the result
  const firstSample = sampling?.samples?.[0];
  const primaryRefLabel = firstSample?.primary_ref != null ? "Record ID" : "Record";
  const entityRefLabel  = firstSample?.entity_ref  != null ? "Entity"    : "Entity";
  const amountRefLabel  = firstSample?.amount_ref  != null ? "Amount"    : "Amount";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="phase-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Phase 6: Sampling Strategy</h2>
        <p className="text-sm text-muted-foreground mt-1">Population is detected automatically from uploaded data, and you control how many samples to generate.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Sampling Parameters</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Requested Sample Size</Label>
              <Input type="number" min="1" value={sampleSize} onChange={e => setSampleSize(parseInt(e.target.value, 10) || 0)}
                data-testid="sample-size-input" className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Sampling Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1" data-testid="sampling-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random</SelectItem>
                  <SelectItem value="systematic">Systematic</SelectItem>
                  <SelectItem value="stratified">Stratified</SelectItem>
                  <SelectItem value="high_value">High Value</SelectItem>
                  <SelectItem value="risk-based">Risk-based</SelectItem>
                  <SelectItem value="judgmental">Judgmental</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Population Fallback (Optional)</Label>
            <Input
              type="number"
              min="1"
              value={populationFallback}
              onChange={e => setPopulationFallback(e.target.value)}
              data-testid="population-fallback-input"
              className="mt-1 font-mono"
              placeholder="Use only if auto-detection cannot find the population"
            />
          </div>
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Population Detection</p>
            <p className="text-sm">
              {sampling?.population_size
                ? `Detected ${sampling.population_size} records in the current audit population.`
                : "Population will be auto-detected when you generate the sample. If detection fails, provide the fallback value above."}
            </p>
          </div>
          <Button onClick={handleGenerate} disabled={generating || sampleSize < 1} data-testid="generate-sampling-btn">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Generate Strategy
          </Button>
        </CardContent>
      </Card>

      {sampling && (
        <>
          <Card>
            <CardHeader><CardTitle className="text-base">Strategy Result</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-muted rounded-md">
                  <p className="text-2xl font-bold font-mono">{sampling.sample_size || sampling.recommended_sample_size || '-'}</p>
                  <p className="text-xs text-muted-foreground">Generated Sample Size</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-md">
                  <p className="text-2xl font-bold font-mono">{sampling.population_size || '-'}</p>
                  <p className="text-xs text-muted-foreground">Population</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-md">
                  <p className="text-2xl font-bold font-mono capitalize">{displayMethod || "-"}</p>
                  <p className="text-xs text-muted-foreground">Method</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted rounded-md">
                  <p className="text-2xl font-bold font-mono">{sampling.recommended_sample_size || '-'}</p>
                  <p className="text-xs text-muted-foreground">Recommended Size</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-md">
                  <p className="text-2xl font-bold font-mono capitalize">{sampling.source || '-'}</p>
                  <p className="text-xs text-muted-foreground">Source</p>
                </div>
              </div>
              {sampling.rationale && (
                <div className="rounded-2xl border border-blue-200/70 bg-gradient-to-r from-blue-50/90 via-indigo-50/70 to-violet-50/80 p-4 shadow-sm dark:border-cyan-300/20 dark:from-cyan-300/10 dark:via-slate-900 dark:to-violet-400/10">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm dark:bg-slate-900">
                      <Sparkles className="h-4 w-4 text-blue-600 dark:text-cyan-300" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700 dark:text-cyan-300">Sampling Insight</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-100">{sampling.rationale}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {sampling.samples?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Sample Selection Table</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sample ID</TableHead>
                      <TableHead>{primaryRefLabel}</TableHead>
                      <TableHead>{entityRefLabel}</TableHead>
                      <TableHead>{amountRefLabel}</TableHead>
                      <TableHead>Selection Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sampling.samples.slice(0, 20).map((s, i) => (
                      <TableRow key={i} className={i % 2 === 0 ? "bg-background/70" : "bg-muted/20"}>
                        <TableCell className="py-3 font-mono text-xs font-semibold">{s.sample_id}</TableCell>
                        <TableCell className="py-3 text-xs">{resolvePrimaryRef(s)}</TableCell>
                        <TableCell className="py-3 text-xs">{resolveEntityRef(s)}</TableCell>
                        <TableCell className="py-3 text-xs font-mono">{resolveAmountRef(s)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{s.selected_reason}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={!sampling} data-testid="continue-to-evidence-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Evidence Evaluation
        </Button>
      </div>
    </div>
  );
}
